import { createRouteRegistry, pathSegment } from "@ngft/react-components-library/notifications";

import crewWebRoutes from "src/shared/api-types/notifications/crew-web-routes.json";

/** What the route builders need from the app: the selected crew (it is the notification's company). */
export type CrewRouteContext = { crewId: string };

/**
 * One builder per route key of the crew/web route catalogue (ngft-api-types
 * `notifications/crew-web-routes.json`), which ngft-api registers in IAM. A notification names only
 * a route key and params, never a URL, so the paths live here. notificationRoutes.test.ts fails when
 * the catalogue gains a route without a builder.
 */
export const CREW_ROUTE_BUILDERS: Record<
  string,
  (context: CrewRouteContext, params: Readonly<Record<string, string>>) => string
> = {
  // My courses: unlike All courses (Moodle only), it also lists custom courses, which get reminders too.
  my_trainings: () => "/user/compliance/positions",
  my_certificates: () => "/user/certificates",
  schedule: () => "/user-trainings",
  crew_overview: (c) => `/crew/${pathSegment(c.crewId)}/overview`,
  certificate_approval: (c) => `/crew/${pathSegment(c.crewId)}/pending`,
  subscriptions: (c) => `/crew/${pathSegment(c.crewId)}/subscriptions`,
  // A declined certificate: the recipient's own courses, or the member's courses when they uploaded it for them.
  certificate_reupload: (c, p) =>
    p.memberId
      ? `/crew/${pathSegment(c.crewId)}/members/${pathSegment(p.memberId)}/compliance/positions`
      : "/user/compliance/positions",
};

export const CREW_WEB_ROUTE_KEYS: string[] = crewWebRoutes.routes.map((r) => r.route);

// At module level: the provider needs `routes` to keep its identity.
export const crewRoutes = createRouteRegistry<CrewRouteContext>({
  routes: Object.fromEntries(
    crewWebRoutes.routes
      .filter(({ route }) => CREW_ROUTE_BUILDERS[route])
      .map(({ route, required, optional }) => [
        route,
        {
          // Params are producer ids; the registry passes only the declared ones to the builder.
          required: Object.fromEntries(required.map((name) => [name, "id" as const])),
          optional: Object.fromEntries(optional.map((name) => [name, "id" as const])),
          build: (params: Readonly<Record<string, string>>, context: CrewRouteContext) =>
            CREW_ROUTE_BUILDERS[route](context, params),
        },
      ]),
  ),
});
